import { CountryCode } from 'lib-address'

import { z } from '@/utils/zod'
import {
    AddressValidationError,
    CountryMissingError,
    InvalidStateError,
    InvalidZipError,
    InvalidZipSubRegionError,
    isValidCountryCode,
    MissingFieldError,
    validateAddress,
} from '@/utils/address'

export type { CountryCode } from 'lib-address'

export const CountrySchema = z
    .string()
    .pipe(
        z
            .custom<CountryCode>((val): val is CountryCode => isValidCountryCode(val as CountryCode))
            .meta({ override: { type: 'string' } })
    )
    .refine(
        (val): val is CountryCode => {
            return isValidCountryCode(val)
        },
        {
            params: {
                i18n: { key: 'custom.invalidCountry' },
            },
        }
    )

export const ADDRESS_LINECHARS_MAX = 45

type AddressCountry<CountryCodes extends readonly CountryCode[] | undefined> =
    CountryCodes extends readonly CountryCode[] ? CountryCodes[number] : CountryCode

const createAddressCountrySchema = <const CountryCodes extends readonly CountryCode[] | undefined = undefined>(
    countries?: CountryCodes
) =>
    CountrySchema.refine(
        (country): country is AddressCountry<CountryCodes> =>
            !countries || countries.some((allowedCountry) => allowedCountry === country),
        {
            params: {
                i18n: { key: 'custom.invalidCountry' },
            },
        }
    )

export const NoValidatingAddressSchema = z.object({
    country: z.string().optional(),
    state: z.string().optional(),
    zip: z.string().optional(),
    city: z.string().optional(),
    addressLine: z.string().optional(),
    details1: z.string().optional(),
    details2: z.string().optional(),
})

export const createRawAddressSchema = <const CountryCodes extends readonly CountryCode[] | undefined = undefined>({
    countries,
}: {
    countries?: CountryCodes
}) =>
    z.object({
        country: createAddressCountrySchema(countries),
        state: z.string().optional(),
        zip: z.string().max(10),
        city: z.string().max(ADDRESS_LINECHARS_MAX),
        addressLine: z.string().max(ADDRESS_LINECHARS_MAX), // Street / Number
        details1: z.string().max(ADDRESS_LINECHARS_MAX).optional(), // ie c/o or company
        details2: z.string().max(ADDRESS_LINECHARS_MAX).optional(), // ie apartment or /co
    })

export const createAddressSchema = <const CountryCodes extends readonly CountryCode[] | undefined = undefined>({
    allowEmpty,
    countries,
}: {
    allowEmpty: boolean
    countries?: CountryCodes
}) =>
    createRawAddressSchema({ countries }).check(({ value, ...ctx }) => {
        if (!isValidCountryCode(value.country)) {
            return
        }

        if (allowEmpty) {
            if (!value.addressLine && !value.city && !value.zip) {
                return
            }
        }

        if (value.zip.length + value.city.length + 1 > ADDRESS_LINECHARS_MAX) {
            ctx.issues.push({
                code: 'too_big',
                maximum: ADDRESS_LINECHARS_MAX - 1 - value.zip.length,
                inclusive: true,
                type: 'string',
                input: value.city,
                origin: typeof value.city,
                path: ['city'],
            })
        }

        try {
            validateAddress({
                country: value.country,
                state: value.state,
                zip: value.zip,
                city: value.city,
                addressLine1: value.addressLine,
            })
            return
        } catch (err) {
            if (err instanceof CountryMissingError) {
                throw err
            }

            if (err instanceof AddressValidationError) {
                err.errors.forEach((e) => {
                    if (e instanceof MissingFieldError) {
                        ctx.issues.push({
                            code: 'invalid_type',
                            expected: 'string',
                            received: 'undefined',
                            input: undefined,
                            path: [e.field],
                        })
                    }

                    if (e instanceof InvalidZipError) {
                        ctx.issues.push({
                            code: 'custom',
                            input: value.zip,
                            params: {
                                i18n: { key: 'custom.invalidZip' },
                            },
                            path: ['zip'],
                        })
                    }

                    if (e instanceof InvalidZipSubRegionError) {
                        ctx.issues.push({
                            code: 'custom',
                            input: value.zip,
                            params: {
                                i18n: { key: 'custom.invalidZip' },
                            },
                            path: ['zip'],
                        })
                    }

                    if (e instanceof InvalidStateError) {
                        ctx.issues.push({
                            code: 'custom',
                            input: value.state,
                            params: {
                                i18n: { key: 'custom.invalidState' },
                            },
                            path: ['state'],
                        })
                    }
                })
            }

            return
        }
    })

export const RawAddressSchema = createRawAddressSchema({})
export const AddressSchema = createAddressSchema({ allowEmpty: false })

export type Address = z.infer<typeof AddressSchema>
